/**
 * Pause / play control for an autoplaying Swiper carousel.
 *
 * WCAG 2.2.2 requires a mechanism to pause, stop or hide anything that moves
 * automatically for more than five seconds. Swiper's `disableOnInteraction`
 * stops rotation once a user touches the arrows, but that is undiscoverable
 * and does nothing for someone who never touches them, so it does not satisfy
 * the criterion on its own.
 *
 * The Swiper instance is read lazily from the target element rather than being
 * passed in at construction: Swiper assigns `element.swiper` in its own
 * constructor, and the sections initialise on `window load`. Looking it up on
 * first click removes any dependency on script ordering.
 *
 * Markup contract:
 *   <swiper-autoplay-control data-swiper-target="#some-id">
 *     <button type="button" class="swiper-autoplay-control__button">…</button>
 *   </swiper-autoplay-control>
 */
class SwiperAutoplayControl extends HTMLElement {
  static pausedModifier = 'swiper-autoplay-control__button--paused';

  constructor() {
    super();
    this.onToggleBound = this.onToggle.bind(this);
    this.onReducedMotionChangeBound = this.onReducedMotionChange.bind(this);
    this.onCarouselFocusInBound = this.onCarouselFocusIn.bind(this);
    this.onCarouselFocusOutBound = this.onCarouselFocusOut.bind(this);
    this.wasStoppedByFocus = false;
  }

  connectedCallback() {
    this.toggleButton = this.querySelector('button');
    if (!this.toggleButton) return;

    this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.toggleButton.addEventListener('click', this.onToggleBound);
    this.reducedMotionQuery.addEventListener('change', this.onReducedMotionChangeBound);

    // The carousel root is rendered before this element, so it is already in
    // the document by the time this runs. Only the Swiper instance hanging off
    // it arrives late, and that is still read lazily.
    this.carouselElement = this.dataset.swiperTarget ? document.querySelector(this.dataset.swiperTarget) : null;
    this.carouselElement?.addEventListener('focusin', this.onCarouselFocusInBound);
    this.carouselElement?.addEventListener('focusout', this.onCarouselFocusOutBound);

    // Sections skip the autoplay config entirely under reduced motion, so the
    // carousel is already stopped and the control must open in its paused state.
    this.setPausedState(this.reducedMotionQuery.matches);
  }

  disconnectedCallback() {
    this.toggleButton?.removeEventListener('click', this.onToggleBound);
    this.reducedMotionQuery?.removeEventListener('change', this.onReducedMotionChangeBound);
    this.carouselElement?.removeEventListener('focusin', this.onCarouselFocusInBound);
    this.carouselElement?.removeEventListener('focusout', this.onCarouselFocusOutBound);
  }

  get swiperInstance() {
    const targetSelector = this.dataset.swiperTarget;
    if (!targetSelector) return null;
    const targetElement = document.querySelector(targetSelector);
    return targetElement ? targetElement.swiper : null;
  }

  get isPaused() {
    return this.toggleButton.classList.contains(SwiperAutoplayControl.pausedModifier);
  }

  onToggle() {
    const swiper = this.swiperInstance;
    if (!swiper || !swiper.autoplay) return;

    // Under reduced motion the section passes `autoplay: false`, which Swiper
    // normalises to an object with no delay. Calling start() in that state
    // schedules on NaN, effectively zero, and the carousel spins as fast as it
    // can render — for the one visitor who asked it not to move at all.
    if (!swiper.params.autoplay?.enabled) return;

    // An explicit press always wins over a pause we applied because focus
    // happened to be inside the carousel.
    this.wasStoppedByFocus = false;

    if (this.isPaused) {
      swiper.autoplay.start();
      this.setPausedState(false);
      this.setLiveRegionPoliteness('off');
    } else {
      swiper.autoplay.stop();
      this.setPausedState(true);
      this.setLiveRegionPoliteness('polite');
    }
  }

  /**
   * Auto-rotation has to stop when anything inside the carousel takes focus,
   * or a keyboard visitor reads a slide while it slides away underneath them.
   * Swiper's autoplay module has no focus handling at all — only
   * pauseOnMouseEnter and visibilitychange — so it has to be added here.
   *
   * This deliberately leaves the button's own state alone. The button reports
   * what the visitor chose, never a pause the page applied on their behalf.
   */
  onCarouselFocusIn() {
    const swiper = this.swiperInstance;
    // `running` is already false under reduced motion and after a manual stop,
    // so this covers both without special-casing either.
    if (!swiper?.autoplay?.running) return;

    // stop(), not pause(): pause() re-starts itself on the next transitionend
    // unless a flag private to Swiper's autoplay module is set, which callers
    // cannot reach.
    swiper.autoplay.stop();
    this.wasStoppedByFocus = true;
    this.setLiveRegionPoliteness('polite');
  }

  onCarouselFocusOut(event) {
    if (!this.wasStoppedByFocus) return;

    // focusout also fires when moving between two links inside the carousel;
    // only resume once focus has genuinely left it.
    if (event.relatedTarget && this.carouselElement?.contains(event.relatedTarget)) return;

    this.wasStoppedByFocus = false;

    // The visitor pressed pause while focus was inside — leave it paused.
    if (this.isPaused) return;

    this.swiperInstance?.autoplay?.start();
    this.setLiveRegionPoliteness('off');
  }

  /**
   * Swiper sets aria-live once when it initialises and never revisits it, so a
   * paused carousel keeps announcing nothing while a rotating one would
   * announce every slide. Track the current state instead.
   */
  setLiveRegionPoliteness(politeness) {
    const wrapperElement = this.swiperInstance?.wrapperEl;
    if (wrapperElement) wrapperElement.setAttribute('aria-live', politeness);
  }

  onReducedMotionChange(event) {
    if (!event.matches) return;
    this.swiperInstance?.autoplay?.stop();
    this.setPausedState(true);
  }

  setPausedState(isPaused) {
    this.toggleButton.classList.toggle(SwiperAutoplayControl.pausedModifier, isPaused);

    // Prefer the labels Liquid put on this instance. The page-global strings
    // are identical for every carousel, so falling back to them here would
    // replace a name that identifies this slideshow with one that does not,
    // the moment the visitor first pressed the button.
    const globalStrings = window.accessibilityStrings || {};
    const label = isPaused
      ? this.dataset.playLabel || globalStrings.playSlideshow
      : this.dataset.pauseLabel || globalStrings.pauseSlideshow;

    // Liquid seeds a correct label on render; only replace it when we actually
    // have something, so we never blank out the accessible name.
    if (label) this.toggleButton.setAttribute('aria-label', label);
  }
}

customElements.define('swiper-autoplay-control', SwiperAutoplayControl);
